import { DOCUMENT_TYPE } from "Common/dictionaries/documentType";
import Menu from "Components/menu";
import { Tab, TabPanel, Tabs } from "Components/tabs";
import classNames from "classnames";
import compact from "lodash/compact";
import filter from "lodash/filter";
import findIndex from "lodash/findIndex";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import map from "lodash/map";
import reject from "lodash/reject";
import startsWith from "lodash/startsWith";
import uniq from "lodash/uniq";
import React, { useCallback, useEffect, useMemo } from "react";
import { ITEM_SIZES, NAV_CENTERED_CONTENT_CLASS_NAME, NAV_MB_DEFAULT_GAP_CLASS_NAME, SidebarMenuItem, SidebarMenuList } from "../../Common";
import { CHILD_KEY_PREFIX, CONTENT_MODE_TAB, FILE_KEY_REFIX, FOLDER_KEY_REFIX, GROUP_KEY_PREFIX, MASTER_KEY_PREFIX } from "../constants";
import buildContentUploadActionsMenuItems from "../utils/buildContentUploadActionsMenuItems";
import buildNavigationContentSidebarItems from "../utils/buildNavigationContentSidebarItems";
import buildNavigationContentUploadItems, { makeFileItemKey, makeFolderItemKey } from "../utils/buildNavigationContentUploadItems";
import { buildNavigationObjectKey } from "../utils/buildNavigationSidebarItems";
import locale from "./tabNavigationContent.locale";
import styles from "./tabNavigationContent.module.css";
export default function TabNavigationContent(props) {
    useEffect(() => {
        props.onInitialized && props.onInitialized();
    }, [props.onInitialized]);
    const sidebarMenuItems = useMemo(() => {
        if (!props.activeDocumentId)
            return [];
        return props.documentType && props.navigationObjects && props.documentObjects
            ? buildNavigationContentSidebarItems(props.documentType, props.documentObjects, props.navigationObjects.content, props.navigationObjects.contentMasterNodes, props.contentChildren)
            : undefined;
    }, [
        props.documentType,
        props.navigationObjects,
        props.documentObjects,
        props.activeDocumentId,
        props.contentChildren
    ]);
    const handleLoadFiles = useCallback((folderId) => props.activeDocumentId &&
        props.onLoadFiles &&
        props.onLoadFiles(folderId, props.activeDocumentId), [props.activeDocumentId, props.onLoadFiles]);
    useEffect(() => {
        if (props.selectedFolderId && handleLoadFiles) {
            const files = reject(props.files, "Deleted");
            if (!files || !files.length) {
                handleLoadFiles(props.selectedFolderId);
            }
        }
    }, [props.files, props.selectedFolderId, handleLoadFiles]);
    const contentUploadMenuItems = useMemo(() => buildNavigationContentUploadItems(props.folders, props.allFiles, (folder) => (React.createElement(Menu, { key: "menu", popoverClassName: "dark-popover1", togglerClassName: "no-border no-default-colors animate-hover-color", togglerIconClassName: "icon-dots", togglerWidth: ITEM_SIZES.MIN_ACTION_ELEMENT_SIZE, togglerHeight: ITEM_SIZES.MIN_ACTION_ELEMENT_SIZE, items: buildContentUploadActionsMenuItems(folder, props.canEditDocuments, props.onOpenFolderEditorPanel, props.onDeleteFolder) }))), [props.folders, props.allFiles, props.onOpenFolderEditorPanel, props.onDeleteFolder]);
    const selectedItems = useMemo(() => new Set([...map(props.selectedIds, buildNavigationObjectKey), ...(props.selectedIds || [])]), [props.selectedIds]);
    const selectedUploadedItems = useMemo(() => new Set(compact([
        props.selectedFolderId && makeFolderItemKey(props.selectedFolderId),
        props.selectedFileId && makeFileItemKey(props.selectedFileId)
    ])), [props.selectedFolderId, props.selectedFileId]);
    const emptyText = !props.activeDocumentId
        ? locale.SELECT_DOCUMENT
        : isEmpty(sidebarMenuItems)
            ? locale.NO_NAVIGATION_OBJECTS
            : "";
    const addBtn = useMemo(() => {
        if (!props.canEditDocuments)
            return undefined;
        const e = new SidebarMenuItem("add-document", locale.ADD_FOLDER, "icon-add font-h3 text-primary-new");
        e.className = classNames(NAV_MB_DEFAULT_GAP_CLASS_NAME, NAV_CENTERED_CONTENT_CLASS_NAME, styles["add-btn"]);
        return e;
    }, []);
    const handleItemClick = useCallback((item) => {
        if (startsWith(item.key, MASTER_KEY_PREFIX)) {
            const nodeId = item.sourceData.NodeId;
            props.onSelectNavigationObjects &&
                props.activeDocumentId &&
                props.onSelectNavigationObjects(props.activeDocumentId, [item.key, nodeId]);
            props.activeDocumentId && props.onLoadContentChildren(props.activeDocumentId, nodeId);
            return;
        }
        if (startsWith(item.key, GROUP_KEY_PREFIX)) {
            props.onSelectNavigationObjects &&
                props.activeDocumentId &&
                props.onSelectNavigationObjects(props.activeDocumentId, [
                    item.key,
                    item.sourceData.object.Id,
                    item.sourceData.node.NodeId
                ]);
            return;
        }
        if (startsWith(item.key, CHILD_KEY_PREFIX)) {
            props.onSelectNavigationObjects &&
                props.activeDocumentId &&
                props.onSelectNavigationObjects(props.activeDocumentId, [
                    item.key,
                    item.sourceData.object.Id,
                    item.sourceData.master.NodeId,
                    item.sourceData.child.NodeId
                ]);
            return;
        }
        if (props.documentType === DOCUMENT_TYPE.CONTENT) {
            if (startsWith(item.key, FOLDER_KEY_REFIX)) {
                const folder = item.sourceData;
                props.onSelectFolder &&
                    folder.DocumentId &&
                    props.onSelectFolder(folder.Id, folder.DocumentId);
                return;
            }
            if (startsWith(item.key, FILE_KEY_REFIX)) {
                const file = item.sourceData;
                props.onSelectFile &&
                    props.activeDocumentId &&
                    file.FolderId &&
                    props.onSelectFile(file.FolderId, props.activeDocumentId, file.Id);
                return;
            }
            if (addBtn && item.key === addBtn.key) {
                !isNil(props.activeDocumentId) &&
                    props.onOpenFolderCreatorPanel &&
                    props.onOpenFolderCreatorPanel(props.activeDocumentId);
                return;
            }
            const sourceDataItems = filter(item.subItems, "sourceData");
            if (sourceDataItems.length) {
                const nodeIds = uniq(compact(map(sourceDataItems, subItem => subItem.sourceData.NodeId)));
                nodeIds.length &&
                    props.onSelectNavigationObjects &&
                    props.activeDocumentId &&
                    props.onSelectNavigationObjects(props.activeDocumentId, nodeIds);
            }
            else {
                const nodeId = item.sourceData.NodeId || item.sourceData.NodeId;
                nodeId &&
                    props.onSelectNavigationObject &&
                    props.activeDocumentId &&
                    props.onSelectNavigationObject(props.activeDocumentId, nodeId);
            }
        }
    }, [
        props.onSelectNavigationObject,
        props.onSelectNavigationObjects,
        props.documentType,
        props.onLoadContentChildren,
        props.activeDocumentId
    ]);
    const sidebarMenuList = useMemo(() => props.contentModeTab === CONTENT_MODE_TAB.FOUND ? (React.createElement(SidebarMenuList, { bgColorDefault: props.bgColorDefault, bgColorActive: props.bgColorActive, inProgress: props.inProgress, sidebarMenuItems: sidebarMenuItems, emptyText: emptyText, selectedItems: selectedItems, activeItems: selectedItems, onItemClick: handleItemClick, onSubmenuShown: handleItemClick, filterText: props.filterText, sortBy: props.sortBy, sortOrder: props.sortOrder, smallMargins: true })) : (React.createElement("div", null)), [
        props.contentModeTab,
        props.bgColorDefault,
        props.bgColorActive,
        props.inProgress,
        props.filterText,
        props.sortBy,
        props.sortOrder,
        sidebarMenuItems,
        emptyText,
        selectedItems,
        handleItemClick
    ]);
    const sidebarMenuListUpload = useMemo(() => props.contentModeTab === CONTENT_MODE_TAB.UPLOAD ? (React.createElement(SidebarMenuList, { bgColorDefault: props.bgColorDefault, bgColorActive: props.bgColorActive, inProgress: props.inProgress, sidebarMenuItems: contentUploadMenuItems, sidebarMenuButton: addBtn, emptyText: emptyText, selectedItems: selectedUploadedItems, activeItems: selectedUploadedItems, onItemClick: handleItemClick, filterText: props.filterText, sortBy: props.sortBy, sortOrder: props.sortOrder, smallMargins: true })) : (React.createElement("div", null)), [
        props.contentModeTab,
        props.bgColorDefault,
        props.bgColorActive,
        props.inProgress,
        props.filterText,
        props.sortBy,
        props.sortOrder,
        contentUploadMenuItems,
        emptyText,
        selectedUploadedItems,
        handleItemClick
    ]);
    const tabs = useMemo(() => [
        {
            id: CONTENT_MODE_TAB.FOUND,
            title: locale.FOUND_CONTENT,
            render: () => sidebarMenuList
        },
        {
            id: CONTENT_MODE_TAB.UPLOAD,
            title: locale.USER_CONTENT,
            render: () => sidebarMenuListUpload
        }
    ], [sidebarMenuList, sidebarMenuListUpload]);
    const handleTabChanged = useCallback((tabIndex) => {
        props.onSelectContentModeTab && props.onSelectContentModeTab(tabs[tabIndex].id);
    }, [props.onSelectContentModeTab]);
    return (React.createElement(Tabs, { className: classNames(props.className, "overflow-hidden"), onTabChange: handleTabChanged, selectedTabIndex: findIndex(tabs, ["id", props.contentModeTab]), containerClassName: "border-bottom-navbar-divider", useGap: false, flex: true }, map(tabs, item => [
        React.createElement(Tab, { title: item.title, className: item.id === props.contentModeTab ? "text-new-primary" : "text-default", key: `tab-${item.id}` }),
        React.createElement(TabPanel, { key: `tab-panel-${item.id}` }, item.render ? item.render() : React.createElement("div", null))
    ])));
}
